import { makeStyles } from '@mui/styles';
import { Box, Button, Tooltip, useMediaQuery } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupsIcon from "@mui/icons-material/Groups";
import { CHART_TAB_BUTTON } from "../../../types/chart-dialog.type";
import { useSelector } from "react-redux";
import BusinessIcon from '@mui/icons-material/Business';
import FacilityList from "./FacilityList";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PopupState, { bindPopover, bindHover } from "material-ui-popup-state";
import HoverPopover from "material-ui-popup-state/HoverPopover";
import TrendMappingSVG from "../../../assets/svgs/TrendMapping.svg.js";
import { useMemo } from "react";

const useStyles = makeStyles(theme => ({
    animatedItem: {
        animation: `$myEffect 100ms ${theme.transitions.easing.easeInOut}`
    },
    "@keyframes myEffect": {
        "0%": {
            opacity: 0,
            transform: "translateY(0%)"
        },
        "100%": {
            opacity: 1,
            transform: "translateY(0)"
        }
    },
}));

const getTabButtonMetrics = (isMobile, isLaptop) => {
    if (isMobile) {
        return {
            buttonSize: 28,
            iconSize: 16,
            buildingIconSize: 14,
            chevronBadgeSize: 10,
        };
    }
    if (isLaptop) {
        return {
            buttonSize: 36,
            iconSize: 20,
            buildingIconSize: 17,
            chevronBadgeSize: 11,
        };
    }
    return {
        buttonSize: 42,
        iconSize: 22,
        buildingIconSize: 19,
        chevronBadgeSize: 12,
    };
};

export default function DetailsCardTab({
    selectedTab,
    handleTabButton,
    tableFilterTypeOptions,
    setSelectedFacility,
    selectedFacility,
    isMobile: isMobileProp = false,
    onOpenTrendMapping
}) {
    const facilities = useSelector((state) => state.facility);
    const activeFacilities = useSelector(state => state.activeFacilities.facilities);
    const classes = useStyles();

    const isMobileQuery = useMediaQuery('(max-width: 768px)');
    const isLaptop = useMediaQuery('(min-width: 769px) and (max-width: 1366px)');
    const isMobile = isMobileProp || isMobileQuery;

    const metrics = useMemo(
        () => getTabButtonMetrics(isMobile, isLaptop),
        [isMobile, isLaptop]
    );

    const circleTabButtonSx = useMemo(
        () => ({
            borderRadius: "50%",
            minWidth: metrics.buttonSize,
            width: metrics.buttonSize,
            height: metrics.buttonSize,
            padding: 0,
            borderWidth: "1px",
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            "& .MuiSvgIcon-root": {
                fontSize: metrics.iconSize,
            },
        }),
        [metrics]
    );

    return (
        <>
            <Button
                color={selectedTab === CHART_TAB_BUTTON.TABLE ? "primary" : "inherit"}
                variant="outlined"
                onClick={() => handleTabButton(CHART_TAB_BUTTON.TABLE)}
                sx={circleTabButtonSx}
            >
                <GroupsIcon
                    color={selectedTab === CHART_TAB_BUTTON.TABLE ? "primary" : "inherit"}
                    sx={{ fontSize: metrics.iconSize }}
                />
            </Button>
            <Button
                color={selectedTab === CHART_TAB_BUTTON.CHART ? "primary" : "inherit"}
                variant="outlined"
                sx={circleTabButtonSx}
                onClick={() => handleTabButton(CHART_TAB_BUTTON.CHART)}
            >
                <TrendingUpIcon
                    color={selectedTab === CHART_TAB_BUTTON.CHART ? "primary" : "inherit"}
                    sx={{ fontSize: metrics.iconSize }}
                />
            </Button>

            {isMobile && onOpenTrendMapping && selectedTab === CHART_TAB_BUTTON.CHART && (
                <Tooltip title="Trend Mapping" arrow>
                    <Button
                        color="inherit"
                        variant="outlined"
                        onClick={onOpenTrendMapping}
                        sx={{
                            ...circleTabButtonSx,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderColor: "rgba(0, 0, 0, 0.23)",
                            "&:hover": {
                                borderColor: "primary.main",
                                "& svg path:not([fill='transparent'])": {
                                    fill: "primary.main",
                                },
                            },
                        }}
                    >
                        <TrendMappingSVG
                            style={{
                                width: metrics.iconSize,
                                height: metrics.iconSize,
                                display: "block",
                                flexShrink: 0,
                            }}
                        />
                    </Button>
                </Tooltip>
            )}

            {activeFacilities && activeFacilities.length > 1 && (
                <PopupState variant="popover" popupId="demo-popup-popover">
                    {(popupState) => (
                        <div>
                            <Button
                                color={selectedTab === CHART_TAB_BUTTON.BUILDING ? "primary" : "inherit"}
                                variant="outlined"
                                sx={{
                                    ...circleTabButtonSx,
                                    position: "relative",
                                    overflow: "visible",
                                }}
                                onClick={() => handleTabButton(CHART_TAB_BUTTON.BUILDING)}
                                {...bindHover(popupState)}
                            >
                                <BusinessIcon
                                    sx={{ fontSize: metrics.buildingIconSize }}
                                    color={
                                        selectedTab === CHART_TAB_BUTTON.BUILDING
                                            ? "primary"
                                            : "inherit"
                                    }
                                />
                                <Box
                                    component="span"
                                    sx={{
                                        position: "absolute",
                                        right: isMobile ? -2 : -1,
                                        bottom: isMobile ? -1 : 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: isMobile ? 11 : 13,
                                        height: isMobile ? 11 : 13,
                                        borderRadius: "50%",
                                        border: "1px solid",
                                        borderColor:
                                            selectedTab === CHART_TAB_BUTTON.BUILDING
                                                ? "primary.main"
                                                : "divider",
                                        bgcolor: "background.paper",
                                        boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
                                        lineHeight: 0,
                                        pointerEvents: "none",
                                    }}
                                >
                                    {popupState.isOpen ? (
                                        <ExpandLessIcon
                                            sx={{ fontSize: metrics.chevronBadgeSize - 1 }}
                                            color={
                                                selectedTab === CHART_TAB_BUTTON.BUILDING
                                                    ? "primary"
                                                    : "inherit"
                                            }
                                        />
                                    ) : (
                                        <ExpandMoreIcon
                                            sx={{ fontSize: metrics.chevronBadgeSize - 1 }}
                                            color={
                                                selectedTab === CHART_TAB_BUTTON.BUILDING
                                                    ? "primary"
                                                    : "inherit"
                                            }
                                        />
                                    )}
                                </Box>
                            </Button>

                            <HoverPopover
                                {...bindPopover(popupState)}
                                className={`${classes.animatedItem}`}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "left"
                                }}
                                transformOrigin={{
                                    vertical: "top",
                                    horizontal: "center"
                                }}
                                PaperProps={{
                                    elevation: 24,
                                    variant: "outlined",
                                    rounded: true,
                                    sx: {
                                        pointerEvents: 'auto',
                                        overflow: 'visible',
                                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                        mt: 0.5,
                                        '&::before': {
                                            content: '""',
                                            display: 'block',
                                            position: 'absolute',
                                            top: 0,
                                            left: "70%",
                                            width: 20,
                                            height: 20,
                                            bgcolor: 'background.paper',
                                            transform: 'translateY(-50%) rotate(45deg)',
                                            zIndexm: 0,
                                        },
                                    }
                                }}
                            >
                                <FacilityList
                                    activeFacilities={activeFacilities}
                                    facilities={facilities}
                                    setSelectedFacility={setSelectedFacility}
                                    selectedFacility={selectedFacility}
                                />
                            </HoverPopover>
                        </div>
                    )}
                </PopupState>
            )}

            {tableFilterTypeOptions && (
                tableFilterTypeOptions
            )}
        </>
    );
}
